import DeleteIcon from "@material-ui/icons/Delete";
import { Box, Button, IconButton, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNotify } from "react-admin";

import CustomCard from "../../components/CustomCard";
import Loader from "../../components/Loader";
import ShowFullTextOnHover from "../../components/ShowFullTextOnHover";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import { firestore } from "../../init/firebase";
import { GenericObject } from "../../types/generic.types";
import { AppResource } from "../../types/resource.types";
import { injectMetadataToSnapshotData } from "../../utils/firebase";
import { entertainmentDocRef } from "../../utils/referencies";
import Client from "./components/Client";
import Vendor from "./components/Vendor";
import { DocReference } from "../../types/firebase.types";
import ConfirmationPopover from "../../components/ConfirmationPopover";

const EntertainmentReviews = () => {
  const [reviews, setReviews] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [disabledLoading, setDisabledLoading] = useState(false);
  const { disabledStyles } = useDisabledOnLoading(disabledLoading);

  const notify = useNotify();
  const handleDelete = async (ref: DocReference, reviewId: string) => {
    setDisabledLoading(true);
    try {
      await ref.delete();
      setReviews((prevState: any) => prevState.filter((review: GenericObject) => review.id !== reviewId));
      notify("Review deleted", "success");
      setDisabledLoading(false);
    } catch (ex: any) {
      console.log(ex.message);
      notify("Error on deleting review", "error");
      setDisabledLoading(false);
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const snapshots = await firestore.collectionGroup(AppResource.entertainmentReviews).limit(20).get();
      if (!snapshots.empty) {
        const data = snapshots.docs.map((doc) => injectMetadataToSnapshotData(doc));
        setReviews(data);
      }
      setLoading(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoading(false);
    }
  };

  const onLoadMore = async () => {
    setDisabledLoading(true);
    try {
      const lastSnapshot = reviews[reviews.length - 1];
      const snapshots = await firestore
        .collectionGroup(AppResource.entertainmentReviews)
        .orderBy("createdAt", "desc")
        .startAfter(lastSnapshot.snapshot)
        .limit(20)
        .get();
      if (!snapshots.empty) {
        const data = snapshots.docs.map((doc) => injectMetadataToSnapshotData(doc));
        setReviews((prevReviewsState: any) => [...prevReviewsState, ...data]);
      }
      setDisabledLoading(false);
    } catch (ex: any) {
      console.log(ex.message);
      setDisabledLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  return (
    <Box style={disabledStyles}>
      <Loader loading={loading}>
        {reviews.length ? (
          <Box>
            {reviews.map((review: GenericObject) => {
              return (
                <Box
                  key={review.id}
                  style={{
                    backgroundColor: "#fff",
                    padding: "10px 15px",
                    marginBottom: 20,
                    boxShadow: `0px 2px 3px #bdbdbd`,
                    borderRadius: 6,
                  }}
                >
                  <Box style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <Typography style={{ width: 175 }}>Entertainment</Typography>
                    <Typography style={{ width: 175 }}>Reviewer</Typography>
                    <Typography style={{ width: 225 }}>Review</Typography>
                    <Typography style={{ width: 70 }}>Rate</Typography>
                    <Typography style={{ width: 175 }}>Created At</Typography>
                    <Typography style={{ width: 50 }}>&nbsp;</Typography>
                  </Box>
                  <Box style={{ display: "flex", justifyContent: "space-between" }}>
                    <Vendor id={review.ref.parent.parent.id} doc={entertainmentDocRef} />
                    <Client id={review.reviewer.id} />
                    <Box style={{ width: 225 }}>
                      <ShowFullTextOnHover source="review" color="textSecondary" record={review} />
                    </Box>
                    <Typography style={{ width: 70 }} color="textSecondary">
                      {review.rate}
                    </Typography>
                    <Typography style={{ width: 175 }} color="textSecondary">
                      {dayjs(review.createdAt.toDate()).format("DD/MM/YYYY - hh:mm a")}
                    </Typography>
                    <Box style={{ width: 50 }}>
                      <ConfirmationPopover
                        onConfirm={() => handleDelete(review.ref, review.id)}
                        message="Are you sure you want to delete review?"
                        confirmationText="Confirm"
                        confirmationColor="secondary"
                        Icon={
                          <IconButton>
                            <DeleteIcon color="error" />
                          </IconButton>
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}

            <Box style={{ textAlign: "center" }}>
              <Button variant="contained" color="secondary" onClick={onLoadMore}>
                Load More
              </Button>
            </Box>
          </Box>
        ) : (
          <CustomCard width="50%">
            <Typography>No Reviews yet!</Typography>
          </CustomCard>
        )}
      </Loader>
    </Box>
  );
};

export default EntertainmentReviews;
